import { useCallback, useMemo } from 'react';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { shallow } from 'zustand/shallow';

import { DriverProps, driversStore, type DriversStoreState } from './DriversStore';
import { selectData, selectById } from '../selectors.ts';

import { useAttach } from '../useAttach';

export function useDriversData() {
  useAttach(driversStore, 'useDriversData');
  return useStoreWithEqualityFn(driversStore.store, selectData, shallow);
}
useDriversData.store = driversStore;

export function useDriversById() {
  useAttach(driversStore, 'useDriversById');
  return useStoreWithEqualityFn(driversStore.store, selectById, shallow);
}
useDriversById.store = driversStore;

export function useDriversByOwnerUri() {
  const drivers = useDriversById();

  return useMemo(() => {
    if (drivers.byId == null) {
      return {
        byOwnerUri: null,
        isLoading: drivers.isLoading,
        error: drivers.error,
      };
    }

    const result: Record<string, Record<string, DriverProps>> = {};

    for (const driver of Object.values(drivers.byId)) {
      result[driver.ownerUri] = result[driver.ownerUri] ?? {};
      result[driver.ownerUri][driver.id] = driver;
    }

    return {
      byOwnerUri: result,
      isLoading: drivers.isLoading,
      error: drivers.error,
    };
  }, [drivers.byId, drivers.isLoading, drivers.error]);
}

export function useDriver({ driverId }: { driverId: string }) {
  useAttach(driversStore, 'useDriver');

  const select = useCallback(
    (state: DriversStoreState) => {
      return {
        driver: state.byId?.[driverId] ?? null,
        driverInstance: state.data?.[driverId] ?? null,
      };
    },
    [driverId]
  );

  return useStoreWithEqualityFn(driversStore.store, select, shallow);
}
useDriver.store = driversStore;

export function useDriversAttach() {
  useAttach(driversStore, 'useDriversAttach');
}
useDriversAttach.store = driversStore;
